import * as React from 'react';
import cx from 'classnames';
import { get } from 'lodash';
import ProgressiveImage from 'components/ProgressiveImage/ProgressiveImage';
import getImageUrl from 'utils/getImageUrl';
import shallowEqual from 'utils/shallowEqual';
import styles from './ImageValueWidget.css';
const IMAGE_QUALITY = 50;
const ASPECT_RATIO = '1:1';
const PRELOAD_IMAGE_SIZE = 100;
const PRELOAD_IMAGE_QUALITY = '50';
const PRELOAD_MIN_HEIGHT = 10;
const ASPECT_RATIO_REGEXP = /(\d+):(\d+)/;
export default class ImageValueWidget extends React.Component {
    constructor(props) {
        super(props);
        this.onActualImgLoad = () => {
            if (typeof this.props.onImageLoad === 'function') {
                this.props.onImageLoad(this.imgRef);
            }
        };
        this.calculateContainerWidth = () => {
            if (!this.container)
                return;
            // we calculate the container width
            const rect = this.container.getBoundingClientRect();
            const { width } = rect;
            if (width > 0) {
                // set the image width to double the width
                // of container (for retina displays)
                this.setState({ imageWidth: Math.round(width * 2) });
            }
        };
        this.containerRef = (c) => {
            this.container = c;
        };
        this.setImageRef = (ref) => {
            const { setRef } = this.props;
            this.imgRef = ref;
            typeof setRef === 'function' && setRef(ref);
        };
        this.getLazyLoadedImageWidth = () => {
            if (this.state.imageWidth !== 0) {
                return;
            }
            return this.calculateContainerWidth();
        };
        this.state = {
            imageWidth: 0
        };
    }
    componentDidMount() {
        this.calculateContainerWidth();
    }
    shouldComponentUpdate(nextProps, nextState) {
        return (get(this.props.image, 'dynamicImageUrl') !== get(nextProps.image, 'dynamicImageUrl') ||
            get(this.props.image, 'url') !== get(nextProps.image, 'url') ||
            !shallowEqual(this.state, nextState));
    }
    componentDidUpdate(prevProps) {
        // This check is required because we use `this.forceUpdate()`.
        // And this can get stuck in an infinite loop
        if (get(this.props.image, 'dynamicImageUrl', '') !== get(prevProps.image, 'dynamicImageUrl', '') ||
            get(this.props.image, 'url') !== get(prevProps.image, 'url')) {
            this.calculateContainerWidth();
        }
    }
    getAspectRatio(image) {
        const { aspectRatio } = image;
        return aspectRatio && ASPECT_RATIO_REGEXP.test(aspectRatio) ? aspectRatio : ASPECT_RATIO;
    }
    render() {
        const { image, imageQuality = IMAGE_QUALITY, classNames = {}, isProgressive = true } = this.props;
        const { imageWidth } = this.state;
        const dynamicImageUrl = get(image, 'dynamicImageUrl');
        const url = get(image, 'url');
        const alternateText = get(image, 'alternateText');
        if (!dynamicImageUrl && !url)
            return null;
        const [aspectW, aspectH] = this.getAspectRatio(image).split(':');
        const aspectWidth = parseInt(aspectW, 10);
        const aspectHeight = parseInt(aspectH, 10);
        const height = Math.round((imageWidth * aspectHeight) / aspectWidth);
        let preloadHeight = Math.round((PRELOAD_IMAGE_SIZE * aspectHeight) / aspectWidth);
        if (preloadHeight < PRELOAD_MIN_HEIGHT)
            preloadHeight = PRELOAD_MIN_HEIGHT;
        /**
         * set the padding top to equal the aspect ratio so that the relatively
         * positioned container has the same height as the containing image height
         */
        const containerStyle = { paddingTop: `${((aspectHeight * 100) / aspectWidth).toFixed(2)}%` };
        const imageSource = getImageUrl(dynamicImageUrl || url, imageWidth.toString(), imageQuality.toString(), height.toString());
        const imgProps = {
            ref: this.setImageRef,
            className: cx(styles['actual-img'], classNames.actualImg),
            alt: '',
            src: imageWidth > 0 && height > 0 ? imageSource : ''
        };
        const nonProgressiveImageClasses = cx(classNames.progressiveContainer, styles['progressive-container'], styles['aspect-image-container']);
        /**
         * We initially render an empty div with padding-top and then detect the conatiner width.
         *
         * After detecting the width, we update the width and then inject the image.
         * The image is positioned absolutely, so as to not cause a jump for the user
         */
        return (<div ref={this.containerRef} className={cx(classNames.main, styles['image-value-widget'])} style={containerStyle}>
				{isProgressive ? (<ProgressiveImage alt={alternateText} preloadSrc={getImageUrl(dynamicImageUrl || url, PRELOAD_IMAGE_SIZE.toString(), PRELOAD_IMAGE_QUALITY, preloadHeight.toString())} containerStyle={containerStyle} className={classNames.actualImg} impressionId={this.props.impressionId} onImageLoad={this.props.onImageLoad} preloadImgClassName={classNames.preloadImg} onLazyLoad={this.getLazyLoadedImageWidth} containerClassName={cx(classNames.progressiveContainer, styles['progressive-container'])} src={imageSource}/>) : (<div className={nonProgressiveImageClasses} style={containerStyle}>
						<img {...imgProps} onLoad={this.onActualImgLoad}/>
					</div>)}
			</div>);
    }
}
